import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useBackHomePath } from "@/lib/useBackHomePath";
import { Layout } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HomeworkTracker, HomeworkLegend } from "@/components/HomeworkTracker";
import { GradeBadge } from "@/components/GradeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CATEGORY_KEYS, CATEGORY_META } from "@/components/CommentCategories";
import { ArrowLeft, Plus, MessageSquarePlus } from "lucide-react";

const STATUSES = ["undelivered", "delivered", "late", "not_good", "copy", "missed"];
const nextStatus = (s) => STATUSES[(STATUSES.indexOf(s) + 1) % STATUSES.length];

const ScoreSelect = ({ value, onChange, testId }) => (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-8 rounded-md border border-slate-300 bg-white text-sm px-2 font-bold text-slate-800" data-testid={testId}>
        <option value="">—</option>
        {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
);

const CycleEditor = ({ studentId, subjectId, semester, cycle, data, computedGrade, hwGrade, onChange, prefix }) => {
    const setHw = async (idx, current) => {
        const ns = nextStatus(current);
        const optimistic = [...data.homework];
        optimistic[idx] = ns;
        onChange({ ...data, homework: optimistic });
        await api.post("/teacher/homework", { student_id: studentId, subject_id: subjectId, semester: parseInt(semester), cycle: parseInt(cycle), index: idx, status: ns });
    };
    const setTest = async (val) => {
        const num = val === "" ? null : Math.max(1, Math.min(5, parseInt(val)));
        onChange({ ...data, test_score: num });
        await api.post("/teacher/test", { student_id: studentId, subject_id: subjectId, semester: parseInt(semester), cycle: parseInt(cycle), test_score: num });
    };
    const setProject = async (val) => {
        const num = val === "" ? null : Math.max(1, Math.min(5, parseInt(val)));
        onChange({ ...data, project_score: num });
        await api.post("/teacher/project", { student_id: studentId, subject_id: subjectId, semester: parseInt(semester), cycle: parseInt(cycle), project_score: num });
    };
    const setOral = async (val) => {
        const num = val === "" ? null : Math.max(1, Math.min(5, parseInt(val)));
        onChange({ ...data, oral_score: num });
        await api.post("/teacher/oral", { student_id: studentId, subject_id: subjectId, semester: parseInt(semester), cycle: parseInt(cycle), oral_score: num });
    };

    return (
        <div className="border border-slate-200 rounded-xl p-4 bg-bone/40">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Cikli</div>
                    <div className="font-heading font-bold text-slate-800">Nota {cycle}</div>
                </div>
                <GradeBadge grade={computedGrade} size="sm" testId={`${prefix}-grade`} />
            </div>
            <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Detyrat — kliko</div>
                <div className="text-[11px] font-bold text-oxford">{hwGrade.toFixed(2)} / 5</div>
            </div>
            <HomeworkTracker statuses={data.homework} interactive onClickDot={(i, s) => setHw(i, s)} testIdPrefix={prefix} />
            <div className="mt-3 grid grid-cols-3 gap-2">
                <label className="flex flex-col items-stretch gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    <span>Testi</span>
                    <ScoreSelect value={data.test_score} onChange={setTest} testId={`${prefix}-test-select`} />
                </label>
                <label className="flex flex-col items-stretch gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    <span>Projekti</span>
                    <ScoreSelect value={data.project_score} onChange={setProject} testId={`${prefix}-project-select`} />
                </label>
                <label className="flex flex-col items-stretch gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold" title="Opsionale — vetëm kur nxënësi pyetet">
                    <span>Goje</span>
                    <ScoreSelect value={data.oral_score} onChange={setOral} testId={`${prefix}-oral-select`} />
                </label>
            </div>
        </div>
    );
};

const AbsenceDialog = ({ studentId, subjectId, subjectName, onAdded }) => {
    const [open, setOpen] = useState(false);
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({ date: today, hours: 1, comment: "", reason: "unexcused" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post("/teacher/absence", { student_id: studentId, subject_id: subjectId, date: form.date, hours: parseInt(form.hours) || 1, comment: form.comment, reason: form.reason });
            setOpen(false); setForm({ date: today, hours: 1, comment: "", reason: "unexcused" }); onAdded && onAdded();
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid={`add-absence-${subjectId}`}>
                    <Plus className="w-3 h-3" /> Mungesë
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Mungesë në {subjectName}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Data</label>
                        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="absence-date" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Orë të humbura</label>
                        <Input type="number" min={1} max={8} value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required data-testid="absence-hours" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Arsyetimi</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => setForm({ ...form, reason: "excused" })} data-testid="absence-reason-excused"
                                className={`px-3 py-2 rounded-lg border-2 text-sm font-semibold transition ${form.reason === "excused" ? "border-sage bg-sage/10 text-sage" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}>
                                Me arsye
                            </button>
                            <button type="button" onClick={() => setForm({ ...form, reason: "unexcused" })} data-testid="absence-reason-unexcused"
                                className={`px-3 py-2 rounded-lg border-2 text-sm font-semibold transition ${form.reason === "unexcused" ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}>
                                Pa arsye
                            </button>
                        </div>
                    </div>
                    <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Komenti (opsional)" data-testid="absence-comment" />
                    <DialogFooter>
                        <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="absence-submit">{busy ? "Duke ruajtur…" : "Ruaj"}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

const CommentDialog = ({ studentId, subjectId, subjectName, onAdded }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ category: "good", text: "" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post("/teacher/comment", { student_id: studentId, subject_id: subjectId, category: form.category, text: form.text });
            setOpen(false); setForm({ category: "good", text: "" }); onAdded && onAdded();
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid={`add-comment-${subjectId}`}>
                    <MessageSquarePlus className="w-3 h-3" /> Koment
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Koment për {subjectName}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Kategoria</label>
                        <div className="grid grid-cols-2 gap-2">
                            {CATEGORY_KEYS.map((k) => {
                                const m = CATEGORY_META[k];
                                const Icon = m.icon;
                                const active = form.category === k;
                                return (
                                    <button key={k} type="button" onClick={() => setForm({ ...form, category: k })}
                                        className={`px-3 py-2 rounded-lg border-2 text-left text-sm font-semibold transition flex items-center gap-2 ${active ? "border-oxford bg-oxford/5" : "border-slate-200 hover:border-slate-300"}`}
                                        data-testid={`comment-cat-${k}`}>
                                        <Icon className="w-4 h-4" /> {m.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Komenti (opsional)</label>
                        <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="P.sh. Nxënësi tregoi shumë interes për materialin" data-testid="comment-text" />
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="comment-submit">{busy ? "Duke ruajtur…" : "Ruaj Komentin"}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

const SubjectRow = ({ record, semester, studentId, refresh }) => {
    const sem = record.semesters[String(semester)];
    const computed = record.computed[String(semester)];
    const update = (cycleKey, newCycle) => { record.semesters[String(semester)][cycleKey] = newCycle; refresh(); };
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`teacher-subject-${record.subject_id}-sem${semester}`}>
            <div className="flex items-start justify-between gap-4 mb-4">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        {record.subject_type === "professional" ? "Profesionale" : "Përgjithshme"}
                    </div>
                    <h3 className="font-heading text-lg font-bold text-slate-900 mt-1 truncate">{record.subject_name}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        {record.absences_hours > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta text-[11px] font-bold border border-terracotta/30">{record.absences_hours} orë mungesë</span>}
                        {record.comments_count > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-seablue/10 text-seablue text-[11px] font-bold border border-seablue/30">{record.comments_count} komente</span>}
                        <AbsenceDialog studentId={studentId} subjectId={record.subject_id} subjectName={record.subject_name} onAdded={refresh} />
                        <CommentDialog studentId={studentId} subjectId={record.subject_id} subjectName={record.subject_name} onAdded={refresh} />
                    </div>
                </div>
                <div className="text-center shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Përfund.</div>
                    <GradeBadge grade={computed.final} size="md" />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CycleEditor studentId={studentId} subjectId={record.subject_id} semester={semester} cycle="1" data={sem["1"]} computedGrade={computed.grade1} hwGrade={computed.hw1} onChange={(c) => update("1", c)} prefix={`teach-hw-${record.subject_id}-s${semester}-c1`} />
                <CycleEditor studentId={studentId} subjectId={record.subject_id} semester={semester} cycle="2" data={sem["2"]} computedGrade={computed.grade2} hwGrade={computed.hw2} onChange={(c) => update("2", c)} prefix={`teach-hw-${record.subject_id}-s${semester}-c2`} />
            </div>
        </div>
    );
};

export default function TeacherStudent() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tick, setTick] = useState(0);
    const backPath = useBackHomePath();

    const fetchData = useCallback(() => {
        api.get(`/students/${id}/records`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [id]);

    useEffect(() => { fetchData(); }, [fetchData]);
    const refresh = () => setTick((t) => t + 1);
    useEffect(() => { if (tick === 0) return; const t = setTimeout(fetchData, 400); return () => clearTimeout(t); }, [tick, fetchData]);

    return (
        <Layout>
            <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-6" data-testid="back-to-teacher">
                <ArrowLeft className="w-4 h-4" /> Mbrapa te Nxënësit
            </Link>
            {loading ? <div className="text-slate-500">Duke ngarkuar…</div> : !data ? <div>Nuk u gjet.</div> : (
                <>
                    <div className="mb-8">
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Edito Notat, Mungesat & Komentet</div>
                        <h1 className="font-heading text-5xl font-black text-slate-900 tracking-tight">{data.student.name}</h1>
                        <p className="text-slate-600 mt-2">Viti {data.student.year} • {data.records.length} lëndë</p>
                    </div>
                    <div className="mb-6"><HomeworkLegend /></div>
                    <Tabs defaultValue="1" className="w-full">
                        <TabsList className="bg-white border border-slate-200 h-11 p-1">
                            <TabsTrigger value="1" data-testid="teacher-semester-1-tab" className="px-6 data-[state=active]:bg-oxford data-[state=active]:text-white">Semestri 1</TabsTrigger>
                            <TabsTrigger value="2" data-testid="teacher-semester-2-tab" className="px-6 data-[state=active]:bg-oxford data-[state=active]:text-white">Semestri 2</TabsTrigger>
                        </TabsList>
                        {["1", "2"].map((s) => (
                            <TabsContent key={s} value={s} className="mt-6">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                    {data.records.map((r) => <SubjectRow key={r.id} record={r} semester={s} studentId={data.student.id} refresh={refresh} />)}
                                </div>
                            </TabsContent>
                        ))}
                    </Tabs>
                </>
            )}
        </Layout>
    );
}
